/**
 * A property's measurements — what the user called takeoffs.
 *
 * They live here rather than on a quote because they are facts about a site,
 * not lines on a document: the lawn is 4,200 sq ft whoever is quoting it and
 * whatever they are quoting. A service priced by the square foot reads this; the
 * measurement itself never becomes a line.
 *
 * Which types exist is a catalogue decision; which of them apply to a site is
 * not. A property with a dozen types on the price list is not a property with
 * a dozen missing measurements — most of them will never apply to it — so the
 * card lists what has been measured and "Add a measurement" offers the rest.
 * Listing every type as blank made a walked, fully-measured site look
 * perpetually unfinished.
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Card } from './primitives.js'
import { ConfirmDialog, SlideOver, Toast } from './overlays.js'
import { MenuSelect } from './menu-select.js'
import { Field } from './fields.js'
import { formatDateOnly, formatNumber } from '../lib/format.js'
import { request } from '../lib/api.js'
import type { PropertyMeasurement } from '../lib/catalog-types.js'
import { RecordCard, RelatedRow } from '../record/record-rail.js'
import { IconPlus, IconRuler } from '../frame/icons.js'

const SOURCES: { value: 'walked' | 'aerial' | 'typed'; label: string }[] = [
  { value: 'walked', label: 'Walked on site' },
  { value: 'aerial', label: 'Off an aerial' },
  { value: 'typed', label: 'Typed from memory' },
]

/**
 * What a source is called on this card.
 *
 * `map_layer` and `mixed` are not choices in the dialog above — nothing
 * types them — but they are what a total made of map parts reads as, and a
 * row that fell through to "Typed" would be saying the opposite of the truth
 * (§10).
 */
const SOURCE_WORDS: Record<string, string> = {
  walked: 'Walked on site',
  aerial: 'Off an aerial',
  typed: 'Typed',
  map_layer: 'From the map',
  mixed: 'Mixed sources',
}

export function PropertyMeasurements({
  propertyId, canEdit,
}: {
  propertyId: string
  canEdit: boolean
}) {
  const [rows, setRows] = useState<PropertyMeasurement[]>([])
  /*
   * One dialog for both adding and editing. Adding used to be a dropdown at
   * the foot of the card that picked a type and *then* opened this — two
   * steps for one decision, and a control in a place no other card has one.
   * Now the card's header has Add like every other card, and the type is the
   * first field of the same form.
   */
  const [editing, setEditing] = useState<PropertyMeasurement | null>(null)
  const [picking, setPicking] = useState(false)
  const [draft, setDraft] = useState({
    value: '', source: 'walked' as 'walked' | 'aerial' | 'typed', measuredOn: '', note: '',
  })
  const [removing, setRemoving] = useState<PropertyMeasurement | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const navigate = useNavigate()

  const load = useCallback(async () => {
    const payload = await request<{ items: PropertyMeasurement[] }>(`/measurements/${propertyId}`)
      .catch(() => ({ items: [] as PropertyMeasurement[] }))
    setRows(payload.items)
  }, [propertyId])

  useEffect(() => { void load() }, [load])

  function close() {
    setEditing(null)
    setPicking(false)
  }

  function openNew() {
    setPicking(true)
    setEditing(null)
    setDraft({ value: '', source: 'walked', measuredOn: new Date().toISOString().slice(0, 10), note: '' })
  }

  function open(row: PropertyMeasurement) {
    setPicking(false)
    setEditing(row)
    setDraft({
      value: row.value ?? '',
      source: (row.source as 'walked' | 'aerial' | 'typed' | null) ?? 'walked',
      measuredOn: row.measuredOn ?? new Date().toISOString().slice(0, 10),
      note: row.note ?? '',
    })
  }

  async function save() {
    if (!editing) return
    setBusy(true)
    try {
      await request(`/measurements/${propertyId}/${editing.measurementTypeId}`, {
        method: 'PUT',
        body: {
          value: Number(draft.value.replace(/[^0-9.]/g, '')) || 0,
          source: draft.source,
          measuredOn: draft.measuredOn || null,
          note: draft.note || null,
        },
      })
      setNotice(`${editing.name} is ${formatNumber(Number(draft.value))} ${editing.unit}.`)
      setEditing(null)
      setPicking(false)
      await load()
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  /*
   * Measured, and measurable. The server returns every active type with a null
   * id for the ones this property has no figure for; the card lists the first
   * set and Add offers the second.
   */
  const measured = rows.filter((row) => row.id)
  const available = rows.filter((row) => !row.id)

  const editor = (
    <SlideOver
      open={editing !== null || picking}
      onClose={close}
      title={editing?.id ? editing.name : 'Add a measurement'}
      width={400}
      footer={
        <div className="flex w-full items-center gap-2">
          {/*
            * Remove lives here, not on the row. The row is a link now — one
            * target, one meaning — and a destructive button sitting on a link
            * is a destructive button somebody hits on the way past.
            */}
          {editing?.id && canEdit && (
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => setRemoving(editing)}
              className="text-danger"
            >
              Remove
            </Button>
          )}
          <span className="ml-auto flex gap-2">
            <Button disabled={busy} onClick={close}>Cancel</Button>
            <Button
              variant="primary"
              disabled={busy || !editing || draft.value.trim() === ''}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </span>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        {/*
          * The type is the first field when adding and a fact when editing.
          * A property is measured for turf area once; changing which type a
          * figure belongs to is not an edit, it is two edits.
          */}
        {!editing?.id && (
          <label className="block">
            <span className="mb-1 block text-sub text-text-muted">What is being measured</span>
            <MenuSelect
              size="field"
              label="Measurement type"
              placeholder="Pick a type"
              value={editing?.measurementTypeId ?? null}
              onChange={(value) => {
                const chosen = available.find((row) => row.measurementTypeId === value)
                if (chosen) setEditing(chosen)
              }}
              options={available.map((row) => ({
                value: row.measurementTypeId, label: row.name, detail: row.unit,
              }))}
              searchPlaceholder="Find a type"
              minWidth={220}
              className="w-full"
            />
          </label>
        )}
      </div>

      {editing && (
        <div className="mt-3 flex flex-col gap-3">
          <Field label={`Measured (${editing.unit})`} value={draft.value} autoFocus
            onChange={(value) => setDraft({ ...draft, value })} />

          <label className="block">
            <span className="mb-1 block text-sub text-text-muted">How it was measured</span>
            <select
              value={draft.source}
              onChange={(event) => setDraft({
                ...draft,
                source: event.currentTarget.value as 'walked' | 'aerial' | 'typed',
              })}
              className="h-9 w-full rounded-field border border-line bg-surface px-2 text-cell text-text"
            >
              {SOURCES.map((source) => (
                <option key={source.value} value={source.value}>{source.label}</option>
              ))}
            </select>
            <span className="mt-1 block text-sub text-text-faint">
              It travels onto the quote with the quantity, so a customer querying the number
              gets told where it came from.
            </span>
          </label>

          <Field label="Measured on" type="date" value={draft.measuredOn}
            onChange={(value) => setDraft({ ...draft, measuredOn: value })} />

          <label className="block">
            <span className="mb-1 block text-sub text-text-muted">Note</span>
            <textarea
              rows={2}
              value={draft.note}
              onChange={(event) => setDraft({ ...draft, note: event.currentTarget.value })}
              placeholder="Excludes the side yard behind the gate"
              className="w-full rounded-field border border-line bg-surface p-2 text-cell text-text placeholder:text-text-faint"
            />
          </label>

          <Card className="p-3">
            <p className="m-0 text-sub text-text-muted">
              Re-measuring replaces this number rather than adding a second one, so a service
              priced by {editing.name.toLowerCase()} always has one answer to read.
            </p>
          </Card>
        </div>
      )}
    </SlideOver>
  )

  const toast = notice === null ? null : (
    <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
      <Toast onDismiss={() => setNotice(null)}>{notice}</Toast>
    </div>
  )

  return (
    <>
      <RecordCard
        title="Measurements"
        count={measured.length}
        action={canEdit && available.length > 0
          ? { label: 'Add', icon: <IconPlus size={13} />, onClick: openNew }
          : undefined}
        /*
         * The note for this pane, at the bottom under a rule — the same place
         * and the same shape as the contacts card's legend.
         */
        footer={rows.length === 0
          ? 'Measurement types are set up in the catalogue. Once one exists, every property can be measured for it.'
          : 'Re-measuring replaces the figure rather than adding a second one.'}
      >
        {measured.length === 0
          ? (
            <p className="m-0 px-[18px] py-4 text-body text-text-muted">
              {rows.length === 0
                ? 'Nothing to measure yet.'
                : 'Not measured yet — a service priced by the square foot reads these.'}
            </p>
          )
          : measured.map((row) => (
            <RelatedRow
              key={row.measurementTypeId}
              mark={(
                <span
                  aria-hidden
                  className="flex h-[26px] w-[26px] items-center justify-center rounded-field
                             bg-accent-soft text-accent"
                >
                  <IconRuler size={14} />
                </span>
              )}
              title={row.name}
              trailing={(
                <span className="tnum text-body text-text">
                  {formatNumber(Number(row.value))}{' '}
                  <span className="text-text-muted">{row.unit}</span>
                </span>
              )}
              sub={[
                SOURCE_WORDS[row.source ?? ''] ?? 'Typed',
                /* Why the row opens the map rather than the dialog. */
                (row.partCount ?? 0) > 0
                  ? `${row.partCount} part${row.partCount === 1 ? '' : 's'} · Map`
                  : null,
                row.measuredOn ? formatDateOnly(row.measuredOn) : null,
                row.measuredByName,
              ].filter(Boolean).join(' · ')}
              /*
               * §10: a total made of parts is edited on the map, not here.
               * The row opens the Map tab's Measurements panel instead of
               * the typed/walked/aerial dialog, because that is where the
               * parts it is the sum of actually are.
               */
              onOpen={() => {
                if ((row.partCount ?? 0) > 0) navigate('?tab=map&panel=measurements')
                else if (canEdit) open(row)
              }}
            />
          ))}
      </RecordCard>

      {editor}

      {removing && (
        <ConfirmDialog
          title={`Remove the ${removing.name} measurement?`}
          busy={busy}
          body={
            <p className="m-0 text-body text-text-muted">
              The type stays in the catalogue — this property just goes back to unmeasured.
              Quotes already priced from it keep their quantities.
            </p>
          }
          onCancel={() => setRemoving(null)}
          onConfirm={() => {
            setBusy(true)
            void request(`/measurements/${propertyId}/${removing.measurementTypeId}`,
              { method: 'DELETE' })
              .then(() => {
                setRemoving(null)
                close()
                setNotice(`Removed the ${removing.name}.`)
                return load()
              })
              .catch((cause: Error) => setNotice(cause.message))
              .finally(() => setBusy(false))
          }}
        />
      )}

      {toast}
    </>
  )
}
